import styled from '@emotion/styled';
import { ResizeObserver } from '@juggle/resize-observer';
import type { FC } from 'react';
import React, { useState } from 'react';
import type { LayerSide } from 'react-laag';
import { useTooltip } from 'react-laag';
import type {
  AnchorEnum,
  Placement,
  RenderLayerProps,
} from 'react-laag/dist/ToggleLayer/types';
import { animated, useSpring } from 'react-spring';
import { Dialog } from '../../components/dialog/Dialog';
import { Divider } from '../../components/divider/Divider';
import { useMedia } from '../../hooks/useMedia';
import { device } from '../../styles/devices';
import { theme } from '../../styles/theme';
import { typography } from '../../styles/typography';
import { shouldForwardProp } from '../../utils';

export interface TooltipProps {
  title?: string;
  copy: React.ReactNode;
  placement?: Placement;
  anchor?: AnchorEnum;
  useQuestionMark?: boolean;
  bg?: string;
}

const Host = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<{
  side: LayerSide;
  isOpen: boolean;
  bg: string;
}>`
  padding: 44px;
  width: 500px;
  background: ${(props) => props.bg};
  z-index: 3;
  pointer-events: ${(props) => (props.isOpen ? 'auto' : 'none')};
  ${(props) => (props.side === 'top' ? 'margin-top: -32px;' : '')};
`;

const Title = styled.h3`
  ${typography.h2}
  margin-top: 80px;
  @media ${device.tablet} {
    ${typography.h4}
    margin-top: 0px;
  }
`;

const Text = styled.p``;

const Triangle = styled.div<{ bg: string }>`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 16px 16px 0 16px;
  border-color: ${(props) => `${props.bg} transparent transparent transparent`};
`;

const Link = styled.a`
  position: relative;
  z-index: 1;
  cursor: pointer;
  display: inline-block;
`;

const ButtonText = styled.span`
  position: relative;
  z-index: 1;
`;

const BorderBottom = styled(animated.div)`
  position: absolute;
  bottom: 2px;
  left: 0;
  z-index: 0;
  height: 4px;
  width: 100%;

  background-color: ${(props) => props.theme.shades.primary2};
  transform-origin: bottom;
`;

const QuestionMark = styled.div`
  background: ${(props) => props.theme.colors.background};
  border-radius: 100px;
  height: 20px;
  width: 20px;
  text-align: center;
  ${typography.bodySmall}
  line-height: 24px;
`;

function getArrowTranslate(layerSide: string) {
  let x = '-50%';
  let y = '0';

  const OFFSET = 8;
  if (layerSide === 'left') {
    x = -OFFSET + 'px';
    y = '-100%';
  } else if (layerSide === 'right') {
    x = OFFSET + 'px';
    y = '-100%';
  }

  const rotation = {
    top: 0,
    right: 90,
    left: -90,
    bottom: 180,
  };

  return `translate(${x}, ${y}) rotate(${rotation[layerSide]}deg)`;
}

interface ContentProps extends RenderLayerProps {
  bg: string;
  title?: string;
  copy: React.ReactNode;
}

function Content({
  isOpen,
  layerProps,
  layerSide,
  bg,
  title,
  copy,
  arrowStyle,
}: ContentProps) {
  const fadeIn = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(15px)' : 'translateY(0px)',
  });
  if (isOpen) {
    return (
      <Host
        ref={layerProps.ref}
        side={layerSide}
        style={
          {
            ...fadeIn,
            ...layerProps.style,
          } as any
        }
        isOpen={isOpen}
        bg={bg}
      >
        <Title>{title}</Title>
        <Divider margin="23px 0 20px" />
        <Text>{copy}</Text>
        <Triangle
          bg={bg}
          style={
            {
              ...arrowStyle,
              top:
                (layerSide === 'left' || layerSide === 'right') &&
                (arrowStyle?.top ?? 0) < 32
                  ? 32
                  : arrowStyle.top,
              position: 'absolute',
              transformOrigin: 'center',
              transform: getArrowTranslate(layerSide),
            } as any
          }
        />
      </Host>
    );
  }
  return null;
}

export const Tooltip: FC<TooltipProps> = function Tooltip({
  title,
  copy,
  placement,
  anchor,
  useQuestionMark,
  bg = theme.shades.primary2,
}) {
  const [clicked, setClicked] = useState(false);
  const isMobile = useMedia('sm');
  const [element, triggerProps] = useTooltip(
    (props) =>
      !isMobile && <Content {...props} bg={bg} title={title} copy={copy} />,
    {
      delayEnter: 100,
      delayLeave: 100,
      ResizeObserver: ResizeObserver,
      closeOnOutsideClick: true,
      closeOnDisappear: 'partial',
      placement: placement ?? {
        anchor: anchor ?? 'TOP_CENTER',
        autoAdjust: true,
        snapToAnchor: false,
        triggerOffset: 16,
        scrollOffset: 16,
        preferX: 'RIGHT',
      },
    },
  );
  /*   const expand = useSpring({
    transform: isHover || isOpen ? 'scaleY(2)' : 'scaleY(1)',
  }); */

  return (
    <>
      {element}
      <Link {...triggerProps}>
        {useQuestionMark ? (
          <QuestionMark onClick={() => setClicked(!clicked)}>?</QuestionMark>
        ) : (
          <>
            <ButtonText>{title}</ButtonText>
            <BorderBottom /* style={expand} */ />
          </>
        )}
      </Link>
      {clicked && isMobile && (
        <Dialog
          onClose={() => setClicked(false)}
          title={{ label: title ?? '' }}
        >
          <Title>{title}</Title>
          <Divider margin="23px 0 20px" />
          <Text>{copy}</Text>
        </Dialog>
      )}
    </>
  );
};
